import { StrictMode, type ComponentType } from 'react'
import { createRoot } from 'react-dom/client'
import { loadConfig } from './lib/config'
import { primeSessionCsrf } from './lib/api'
import { loadI18n } from './lib/i18n'
import { localeFromPath, storedLocale, rememberLocale } from './lib/locale'

// ENG-111: language lives in the URL path (/en, /pl). If a request lands without
// a valid locale prefix, redirect once to the stored/default locale so the rest
// of the app (router basename + i18n) can assume a prefix. URL is the single
// source of truth → a refresh keeps the language.
//
// ENG-170 final phase: this sequence is shared by the standalone shell
// (src/main.tsx) and ai-assessment's Rails-served entrypoint, so the two can
// never drift on boot order — which matters, because loadConfig() and
// primeSessionCsrf() both have to finish before the first component renders.
// Only the default API is primed here; module APIs (Second Brain) get their
// token on first use, so their cold start never delays the first render.
export function bootstrapApp(App: ComponentType<{ basename: string }>): void {
  const locale = localeFromPath(window.location.pathname)
  if (!locale) {
    const target = storedLocale()
    window.location.replace(
      `/${target}${window.location.pathname}${window.location.search}${window.location.hash}`,
    )
    return
  }

  // Remember every visited locale (no exceptions) so a later bare-URL visit lands
  // back in the language last used in this browser.
  rememberLocale(locale)
  // Keep the document language in sync for a11y / SEO (the HTML shell is static en).
  document.documentElement.lang = locale

  const el = document.getElementById('root')
  if (!el) return

  // ENG-201: resolve the module → API base map before the first render, so no
  // component can fire a request against a base that hasn't been decided yet.
  Promise.all([loadI18n(locale), loadConfig().then(() => primeSessionCsrf())])
    .then(() => {
      createRoot(el).render(
        <StrictMode>
          <App basename={`/${locale}`} />
        </StrictMode>,
      )
    })
}
